import React from 'react';
import {
  Box,
  Button,
  Heading,
  Skeleton,
  Stack,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { MdArrowBack } from 'react-icons/md';
import InspectionForm from 'components/inspections/InspectionForm';
import Surface from 'components/ui/Surface';
import useUpdateVehicleInspection from 'hooks/useUpdateVehicleInspection';
import { inspectionKeys } from 'hooks/useVehicleInspections';
import inspectionApi from 'services/inspection.api';

export default function EditInspection() {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const mutedText = useColorModeValue('secondaryGray.700', 'secondaryGray.500');
  const detailQuery = useQuery({
    queryKey: inspectionKeys.detail(uuid),
    queryFn: () => inspectionApi.getDetail(uuid),
    enabled: Boolean(uuid),
  });
  const updateMutation = useUpdateVehicleInspection();

  const handleSubmit = (values) => {
    updateMutation.mutate(
      { uuid, values },
      {
        onSuccess: () => {
          toast({
            title: 'Inspection updated',
            status: 'success',
            borderRadius: '18px',
          });
          navigate(`/admin/inspections/${uuid}`);
        },
      },
    );
  };

  return (
    <Box pt={{ base: '118px', md: '8px', sm: '10px' }}>
      <Stack spacing="20px" mb="28px">
        <Button
          as={RouterLink}
          to={`/admin/inspections/${uuid}`}
          alignSelf="flex-start"
          variant="subtle"
          leftIcon={<MdArrowBack />}
        >
          Back to Detail
        </Button>
        <Text
          textTransform="uppercase"
          letterSpacing="0.18em"
          fontSize="xs"
          color={mutedText}
          fontWeight="700"
        >
          Update Inspection
        </Text>

        <Heading size="2xl">Edit Inspection</Heading>
        <Text color={mutedText} fontSize="lg">
          Replace vehicle photos, update the VIN number, or edit remarks.
        </Text>
      </Stack>
      <Skeleton isLoaded={!detailQuery.isLoading} borderRadius="24px">
        <Surface p={{ base: '22px', xl: '30px' }}>
          <InspectionForm
            mode="edit"
            initialInspection={detailQuery.data?.data}
            isSubmitting={updateMutation.isPending}
            errorMessage={
              updateMutation.error?.response?.data?.error?.message ||
              updateMutation.error?.message
            }
            onSubmit={handleSubmit}
          />
        </Surface>
      </Skeleton>
    </Box>
  );
}
